import { useEffect } from "react";
import { useLang } from "../../i18n/LanguageContext";
import { Reveal, SectionHead, Crosshair } from "./Reveal";
import { ArrowDownRight } from "lucide-react";
import { DiscoveryBooking } from "./DiscoveryBooking";

export const PartnersPage = () => {
  const { t } = useLang();
  const p = t.partners;

  useEffect(() => {
    const id = window.location.hash.slice(1);
    if (id) setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 400);
    else window.scrollTo(0, 0);
  }, []);

  return (
    <main data-testid="partners-page">
      <section className="relative bg-canvas bp-grid pt-[68px]">
        <div className="mx-auto max-w-[1440px] px-5 sm:px-8 pt-16 sm:pt-24 pb-16">
          <div className="max-w-3xl">
            <Reveal>
              <p className="eyebrow flex items-center gap-3" data-testid="partners-eyebrow">
                <span className="inline-block h-px w-10 bg-red-brand" />
                {p.eyebrow}
              </p>
            </Reveal>
            <Reveal delay={0.08}>
              <h1
                data-testid="partners-title"
                className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05] text-ink"
              >
                {p.title}
              </h1>
            </Reveal>
            <Reveal delay={0.16}>
              <p className="mt-6 max-w-xl text-sm sm:text-base leading-relaxed text-[#525964]">{p.desc}</p>
            </Reveal>
            <Reveal delay={0.24}>
              <div
                data-testid="partners-note"
                className="mt-8 flex max-w-xl items-center gap-3 border border-dashed border-[rgba(122,22,32,0.4)] bg-white px-5 py-4"
              >
                <span className="pulse-dot inline-block h-2 w-2 shrink-0 rounded-full bg-red-brand" />
                <p className="font-mono-tech text-[11px] uppercase tracking-[0.12em] text-[#525964]">{p.note}</p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="relative bg-sand border-y border-technical py-20 sm:py-28">
        <div className="mx-auto max-w-[1440px] px-5 sm:px-8">
          <Reveal>
            <p className="eyebrow" data-testid="partners-steps-eyebrow">{p.stepsEyebrow}</p>
          </Reveal>
          <div className="mt-10 relative">
            <div className="absolute left-[27px] sm:left-[35px] top-0 bottom-0 w-px bg-[rgba(22,24,27,0.15)]" />
            <div className="space-y-10">
              {p.steps.map((s, i) => (
                <Reveal key={s.num} delay={i * 0.06}>
                  <div data-testid={`partner-step-${i}`} className="relative flex gap-6 sm:gap-10 group">
                    <div className="relative z-10 flex h-14 w-14 sm:h-[72px] sm:w-[72px] shrink-0 items-center justify-center border border-technical bg-white font-mono-tech text-lg sm:text-2xl font-bold text-red-brand transition-colors duration-300 group-hover:bg-red-brand group-hover:text-[#f8f7f4]">
                      {s.num}
                    </div>
                    <div className="flex-1 border border-technical bg-white p-6 sm:p-8 transition-all duration-300 group-hover:-translate-y-0.5 group-hover:border-[rgba(122,22,32,0.4)] group-hover:shadow-[0_14px_32px_-16px_rgba(22,24,27,0.3)]">
                      <h3 className="font-display text-xl sm:text-2xl font-bold tracking-tight text-ink">{s.title}</h3>
                      <p className="mt-3 max-w-2xl text-sm leading-relaxed text-[#525964]">{s.desc}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="relative bg-canvas bp-grid py-20 sm:py-28">
        <div className="mx-auto max-w-[1440px] px-5 sm:px-8">
          <SectionHead eyebrow={p.audiencesEyebrow} title={p.audiencesTitle} testid="partners-audiences" />
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {p.audiences.map((a, i) => (
              <Reveal key={a.title} delay={i * 0.07}>
                <article
                  data-testid={`partner-audience-${i}`}
                  className="group relative h-full border border-technical bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:border-[rgba(122,22,32,0.4)] hover:shadow-[0_18px_40px_-18px_rgba(22,24,27,0.35)]"
                >
                  <Crosshair className="top-2.5 right-2.5 opacity-40 group-hover:opacity-100 transition-opacity duration-300" />
                  <h3 className="font-display text-lg font-bold tracking-tight text-ink group-hover:text-red-brand transition-colors duration-300">
                    {a.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-[#525964]">{a.desc}</p>
                </article>
              </Reveal>
            ))}
            <Reveal delay={0.35}>
              <a
                data-testid="partners-cta-card"
                href="/#contact"
                className="chamfer group relative flex h-full min-h-[140px] flex-col justify-between bg-red-brand p-7 transition-all duration-300 hover:-translate-y-1 hover:bg-[#5f1019]"
              >
                <span className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-[#f8f7f4]/70">{p.ctaTitle}</span>
                <span className="mt-4 flex items-center justify-between gap-3 font-display text-lg font-extrabold tracking-tight text-[#f8f7f4]">
                  {p.cta}
                  <ArrowDownRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1 group-hover:translate-y-1" />
                </span>
              </a>
            </Reveal>
          </div>
          <Reveal delay={0.2}>
            <p className="mt-10 max-w-xl text-sm leading-relaxed text-[#525964]">{p.ctaDesc}</p>
          </Reveal>
        </div>
      </section>

      <section className="relative bg-sand border-y border-technical py-20 sm:py-28" data-testid="engagement-section">
        <div className="mx-auto max-w-[1440px] px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
          <div>
            <SectionHead eyebrow={p.engEyebrow} title={p.engTitle} desc={p.engDesc} testid="engagement" />
            <ul className="mt-9 border border-technical bg-white">
              {p.services.map((s, i) => (
                <li key={s} data-testid={`engagement-service-${i}`} className="flex items-center gap-4 border-b border-technical px-5 py-4 last:border-b-0">
                  <span className="font-mono-tech text-[10px] font-semibold text-red-brand">{String(i + 1).padStart(2, "0")}</span>
                  <span className="text-sm font-medium text-ink">{s}</span>
                </li>
              ))}
            </ul>
          </div>
          <Reveal delay={0.15}>
            <div className="chamfer bg-ink p-8 sm:p-10" data-testid="compensation-card">
              <p className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-[#e63946]">{p.compEyebrow}</p>
              <h3 className="mt-4 font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-[#f8f7f4]">{p.compTitle}</h3>
              <p className="mt-4 text-sm leading-relaxed text-[#f8f7f4]/60">{p.compDesc}</p>
              <div className="mt-7 flex flex-wrap gap-2.5">
                {["EQUITY", "RETAINER", "3+ YRS"].map((chip) => (
                  <span key={chip} className="border border-[#f8f7f4]/20 px-3.5 py-1.5 font-mono-tech text-[10px] tracking-[0.18em] text-[#f8f7f4]/70">
                    {chip}
                  </span>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="relative bg-canvas bp-grid py-20 sm:py-28" data-testid="pilot-section">
        <div className="mx-auto max-w-[1440px] px-5 sm:px-8">
          <SectionHead eyebrow={t.pilot.eyebrow} title={t.pilot.title} testid="pilot" />
          <div className="mt-12 grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-px bg-[rgba(22,24,27,0.12)] border border-technical">
            {t.pilot.stats.map((s, i) => (
              <Reveal key={s.label} delay={i * 0.06} className="bg-canvas p-6">
                <div className="font-display text-2xl sm:text-3xl font-black tracking-tight text-ink" data-testid={`pilot-stat-${i}`}>{s.val}</div>
                <div className="mt-1.5 font-mono-tech text-[9px] uppercase tracking-[0.15em] text-[#525964]">{s.label}</div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <p className="mt-8 max-w-3xl text-sm leading-relaxed text-[#525964]">{t.pilot.note}</p>
          </Reveal>
        </div>
      </section>
      <DiscoveryBooking />
    </main>
  );
};
